import { useEffect, useRef } from 'react';

import type { RefObject } from 'react';

const SCROLLABLE_CONTAINER_SELECTOR = '.overflow-x-auto';

interface UseScrollSyncReturn {
    /** Ref to attach to the container that contains the BarChart */
    chartContainerRef: RefObject<HTMLDivElement | null>;
    /** Ref to attach to the scrollable tooltip overlay */
    tooltipOverlayRef: RefObject<HTMLDivElement | null>;
    /** Handler to call on scroll event of the tooltip overlay */
    handleTooltipScroll: () => void;
}

/**
 * Custom hook to synchronize scroll between the tooltip overlay and the BarChart
 * Bidirectional: scrolling either container syncs the other
 *
 * @returns Refs and scroll handler for synchronization
 */
export const useScrollSync = (): UseScrollSyncReturn => {
    const chartContainerRef = useRef<HTMLDivElement>(null);
    const tooltipOverlayRef = useRef<HTMLDivElement>(null);
    const isSyncing = useRef(false);

    const handleTooltipScroll = () => {
        if (isSyncing.current) return;
        isSyncing.current = true;

        const barChartScrollContainer = chartContainerRef.current?.querySelector(
            SCROLLABLE_CONTAINER_SELECTOR,
        );

        if (barChartScrollContainer && tooltipOverlayRef.current) {
            barChartScrollContainer.scrollLeft = tooltipOverlayRef.current.scrollLeft;
        }

        requestAnimationFrame(() => {
            isSyncing.current = false;
        });
    };

    useEffect(() => {
        const chartScrollContainer = chartContainerRef.current?.querySelector(
            SCROLLABLE_CONTAINER_SELECTOR,
        );

        if (!chartScrollContainer) return;

        const handleChartScroll = () => {
            if (isSyncing.current) return;
            isSyncing.current = true;

            if (tooltipOverlayRef.current) {
                tooltipOverlayRef.current.scrollLeft = chartScrollContainer.scrollLeft;
            }

            requestAnimationFrame(() => {
                isSyncing.current = false;
            });
        };

        chartScrollContainer.addEventListener('scroll', handleChartScroll);

        return () => {
            chartScrollContainer.removeEventListener('scroll', handleChartScroll);
        };
    }, []);

    return {
        chartContainerRef,
        tooltipOverlayRef,
        handleTooltipScroll,
    };
};
